import { useCallback, useEffect, useRef, useState } from 'react';

export interface IStopwatchTimerParams {
  /**
   * Default: 1000.
   * Interval between UI refreshes in milliseconds.
   * This controls how often React re-renders; elapsed time is always derived from a
   * monotonic clock (performance.now()), not from counting ticks.
   */
  interval?: number;
  /**
   * Default: false.
   * If true, the stopwatch is running on mount from {@link IStopwatchTimerParams.initialElapsedMs} (or 0).
   */
  autostart?: boolean;
  /**
   * Default: 0.
   * Elapsed time in milliseconds when the hook mounts (and after each reset of internal baseline),
   * before any running segment is applied.
   */
  initialElapsedMs?: number;
  /**
   * Callback fired when the reset method is called.
   */
  onReset?: () => void;
}

export type StopwatchTimerResults = {
  /**
   * Elapsed time in milliseconds (derived from monotonic timestamps while running).
   */
  elapsed: number;
  /**
   * True while the stopwatch is accumulating time.
   */
  isRunning: boolean;
  /**
   * Start or resume: begins a running segment if currently stopped. No-op if already running.
   * Does not clear elapsed time; use {@link StopwatchTimerResults.reset} to return to zero.
   */
  start: () => void;
  /**
   * Pause: commits the current running segment into elapsed time and stops.
   */
  pause: () => void;
  /**
   * Reset: sets elapsed to zero, stops the stopwatch, and clears any active running segment.
   */
  reset: () => void;
};

const now = (): number => performance.now();

/**
 * Stopwatch backed by performance.now(); interval only drives display updates.
 */
export function useStopwatchTimer({
  interval = 1000,
  autostart = false,
  initialElapsedMs = 0,
  onReset,
}: IStopwatchTimerParams = {}): StopwatchTimerResults {
  // Accumulated time from all completed running segments; committed on pause and reset.
  const committedElapsedMsRef = useRef<number>(Math.max(0, initialElapsedMs));

  // Timestamp when the current running segment started; null when paused.
  const segmentStartRef = useRef<number | null>(autostart ? now() : null);

  const [isRunning, setIsRunning] = useState<boolean>(autostart);
  const [, triggerRender] = useState(0);

  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => triggerRender(n => n + 1), interval);
    return () => clearInterval(id);
  }, [isRunning, interval]);

  const elapsed =
    isRunning && segmentStartRef.current !== null
      ? committedElapsedMsRef.current + (now() - segmentStartRef.current)
      : committedElapsedMsRef.current;

  const start = useCallback((): void => {
    if (isRunning) return;
    segmentStartRef.current = now();
    setIsRunning(true);
  }, [isRunning]);

  const pause = useCallback((): void => {
    if (!isRunning || segmentStartRef.current === null) return;
    committedElapsedMsRef.current += now() - segmentStartRef.current;
    segmentStartRef.current = null;
    setIsRunning(false);
  }, [isRunning]);

  const reset = useCallback((): void => {
    committedElapsedMsRef.current = 0;
    segmentStartRef.current = null;
    setIsRunning(false);
    onReset?.();
  }, [onReset]);

  return { elapsed, isRunning, start, pause, reset };
}
